import * as React from 'react';

import { useNumericQueryParam } from '../../main/helpers/numeric-query-param-helper';
import { useTabIndexInUrl } from '../../main/helpers/tab-index-in-url-helper';
import { SetOptionalState } from '../../main/models/react-model';
import { CategoryComboContainer } from '../../entry/containers/category-combo-container';
import {
  EVALUATOR_STORY_LISTING_TYPES,
  EvaluatorCategoryDetail,
  EvaluatorStoryListingType,
} from '../models/stories-evaluation-model';

interface EvaluationPanelContextValue {
  selectedCategoryID?: number;
  setSelectedCategoryID: (id?: number) => void;
  renderCategoryCombo: () => JSX.Element;
  setActivatedTabIndex: (index: number) => void;
  filterTab: EvaluatorStoryListingType;
  activatedTabIndex: number;
  idAvaliador?: number;
  setIdAvaliador: SetOptionalState<number>;
  evaluatorCategories?: EvaluatorCategoryDetail[];
  setEvaluatorCategories: SetOptionalState<EvaluatorCategoryDetail[]>;
  detailsRefreshToken: number;
  refreshEvaluatorDetails: () => void;
}

const EvaluationPanelContext = React.createContext<EvaluationPanelContextValue>(
  {
    selectedCategoryID: undefined,
    setSelectedCategoryID: () => undefined,
    renderCategoryCombo: () => <React.Fragment />,
    setActivatedTabIndex: () => undefined,
    filterTab: EVALUATOR_STORY_LISTING_TYPES[0],
    activatedTabIndex: 0,
    idAvaliador: undefined,
    setIdAvaliador: () => undefined,
    evaluatorCategories: undefined,
    setEvaluatorCategories: () => undefined,
    detailsRefreshToken: 0,
    refreshEvaluatorDetails: () => undefined,
  },
);

export const EvaluationPanelProvider: React.FC = ({ children }) => {
  const [selectedCategoryID, setSelectedCategoryID] =
    useNumericQueryParam('categoria');
  const [activatedTabIndex, setActivatedTabIndex] = useTabIndexInUrl();
  const [idAvaliador, setIdAvaliador] = React.useState<number>();
  const [evaluatorCategories, setEvaluatorCategories] =
    React.useState<EvaluatorCategoryDetail[]>();
  const [detailsRefreshToken, setDetailsRefreshToken] = React.useState(0);

  const refreshEvaluatorDetails = React.useCallback(
    () => setDetailsRefreshToken((value) => value + 1),
    [],
  );

  const filterTab = React.useMemo(
    () => EVALUATOR_STORY_LISTING_TYPES[activatedTabIndex],
    [activatedTabIndex],
  );

  const renderCategoryCombo = React.useCallback(
    () => (
      <CategoryComboContainer
        initialSelectedId={selectedCategoryID}
        isOccupationalScopeIdRequired={false}
        hasLabel={false}
        onChange={(selectedId) => {
          if (!selectedId) return;
          setSelectedCategoryID(selectedId);
        }}
      />
    ),
    [selectedCategoryID, setSelectedCategoryID],
  );

  return (
    <EvaluationPanelContext.Provider
      value={{
        selectedCategoryID,
        activatedTabIndex,
        filterTab,
        idAvaliador,
        evaluatorCategories,
        setEvaluatorCategories,
        detailsRefreshToken,
        refreshEvaluatorDetails,
        setSelectedCategoryID,
        renderCategoryCombo,
        setActivatedTabIndex,
        setIdAvaliador,
      }}
    >
      {children}
    </EvaluationPanelContext.Provider>
  );
};

export const useEvaluationPanelContext = () =>
  React.useContext(EvaluationPanelContext);
